import React, { useState } from 'react';
import { db, storage, auth } from '../firebase';
import { collection, addDoc, query, where, getDocs } from 'firebase/firestore'; 
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useNavigate } from 'react-router-dom';
import { statesAndDistricts } from '../data/indianStates';
import YouTubeHelpButton from '../components/YouTubeHelpButton';

const skillCategories = [
  "Electrician", "Plumber", "Carpenter", "Painter", 
  "Rajmistri (Mason)", "Labour", "Welder", "Tiles Mistry", 
  "Driver", "Mechanic", "Pop","Gardner",
  "Hotel Worker" , "Ac & Fridge Machenic", "WashingMachine Machenic" , "WatchMan"
];

const AddWorker = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [image, setImage] = useState(null);
  
  const [showWarning, setShowWarning] = useState(false);

  const [formData, setFormData] = useState({
    name: '', mobile: '', skills: [], experience: '', 
    state: '', district: '', village: '', wage: ''
  });

  const [availableDistricts, setAvailableDistricts] = useState([]);
  
  // ✅ NEW: Search Inputs for Dropdowns
  const [stateSearch, setStateSearch] = useState('');
  const [districtSearch, setDistrictSearch] = useState('');

  // --- HANDLERS ---
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    if (name === 'state') {
      setAvailableDistricts(statesAndDistricts[value] || []);
      setDistrictSearch(''); // Reset district search on state change
    }
  };

  const toggleSkill = (skill) => {
    let newSkills = [...formData.skills];
    if (newSkills.includes(skill)) {
      newSkills = newSkills.filter(s => s !== skill); 
    } else {
      newSkills.push(skill); 
    }
    setFormData({ ...formData, skills: newSkills });
  };

  const handleImageChange = (e) => {
    if (e.target.files[0]) setImage(e.target.files[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.skills.length === 0) return alert("Please select at least one skill.");
    
    setLoading(true);

    try {
      const q = query(collection(db, "workers"), where("mobile", "==", formData.mobile));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        setLoading(false);
        setShowWarning(true); 
        return; 
      }

      let imageUrl = "";
      if (image) {
        const imageRef = ref(storage, `workers/${Date.now()}_${image.name}`);
        await uploadBytes(imageRef, image);
        imageUrl = await getDownloadURL(imageRef);
      }

      await addDoc(collection(db, "workers"), {
        ...formData,
        imageUrl,
        createdBy: auth.currentUser ? auth.currentUser.uid : "guest",
        createdAt: new Date(),
        skills: formData.skills,
        experience: formData.experience 
      });

      alert("Worker Added Successfully! 🎉");
      navigate('/');
      
    } catch (error) {
      console.error("Error adding worker: ", error);
      alert("Error adding worker");
    }
    setLoading(false);
  };

  // Filtering Logic
  const filteredStatesList = Object.keys(statesAndDistricts).filter(state => 
    state.toLowerCase().includes(stateSearch.toLowerCase())
  );

  const filteredDistrictsList = availableDistricts.filter(dist => 
    dist.toLowerCase().includes(districtSearch.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-slate-50 p-6 flex justify-center relative py-20">
        
      {/* DUPLICATE WARNING POPUP */}
      {showWarning && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in">
           <div className="bg-white rounded-3xl p-8 w-full max-w-sm text-center shadow-2xl border-t-4 border-red-500 transform scale-100 transition-all">
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center text-4xl mx-auto mb-4 animate-bounce">
                ⚠️
              </div>
              <h2 className="text-2xl font-extrabold text-slate-800 mb-2">Worker Already Registered!</h2>
              <p className="text-slate-500 mb-6">
                Mobile number <span className="font-bold text-slate-800">{formData.mobile}</span> is already in our list. Please use a different number.
              </p>
              <button onClick={() => setShowWarning(false)} className="w-full bg-red-500 text-white py-3 rounded-xl font-bold shadow-lg hover:bg-red-600 transition">
                Okay, I'll Check
              </button>
           </div>
        </div>
      )}

      <div className="w-full max-w-2xl bg-white rounded-3xl shadow-xl p-8 border border-slate-100">
        <h2 className="text-3xl font-extrabold text-slate-800 mb-6">🛠️ Add Worker Profile</h2>

        <form onSubmit={handleSubmit} className="space-y-6">
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input name="name" placeholder="Worker Name" onChange={handleChange} className="p-4 rounded-xl bg-slate-50 border border-slate-200 outline-none focus:ring-2 focus:ring-blue-500" required />
            <input name="mobile" type="number" placeholder="Mobile Number" onChange={handleChange} className="p-4 rounded-xl bg-slate-50 border border-slate-200 outline-none focus:ring-2 focus:ring-blue-500" required />
          </div>

          <div>
            <label className="block text-sm font-bold text-slate-500 mb-2">Experience (Years)</label>
            <input name="experience" type="number" placeholder="e.g. 5" onChange={handleChange} className="w-full p-4 rounded-xl bg-slate-50 border border-slate-200 outline-none focus:ring-2 focus:ring-blue-500" required />
          </div>

          <div>
            <label className="block text-sm font-bold text-slate-500 mb-2">Select Skills (Click multiple)</label>
            <div className="flex flex-wrap gap-2">
              {skillCategories.map(skill => (
                <button
                  key={skill}
                  type="button"
                  onClick={() => toggleSkill(skill)}
                  className={`px-4 py-2 rounded-full text-sm font-bold border transition-all ${
                    formData.skills.includes(skill) 
                    ? 'bg-blue-600 text-white border-blue-600 shadow-md transform scale-105' 
                    : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                  }`}
                >
                  {formData.skills.includes(skill) ? '✓ ' : '+ '} {skill}
                </button>
              ))}
            </div>
          </div>

          {/* ✅✅✅ NEW SEARCHABLE LOCATION SECTION ✅✅✅ */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            
            {/* STATE */}
            <div className="bg-slate-50 p-2 rounded-xl border border-slate-200">
                <input 
                    type="text" 
                    placeholder="🔎 Type State to Search..." 
                    value={stateSearch}
                    onChange={(e) => setStateSearch(e.target.value)}
                    className="w-full bg-white p-2 rounded-lg mb-2 text-sm outline-none border border-slate-200 text-slate-600"
                />
                <select name="state" onChange={handleChange} className="w-full p-3 rounded-lg bg-white border border-slate-200 outline-none font-bold" required>
                    <option value="">👇 Select State</option>
                    {filteredStatesList.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
            </div>

            {/* DISTRICT */}
            <div className="bg-slate-50 p-2 rounded-xl border border-slate-200">
                <input 
                    type="text" 
                    placeholder="🔎 Type District to Search..." 
                    value={districtSearch}
                    onChange={(e) => setDistrictSearch(e.target.value)}
                    className="w-full bg-white p-2 rounded-lg mb-2 text-sm outline-none border border-slate-200 text-slate-600"
                />
                <select name="district" onChange={handleChange} className="w-full p-3 rounded-lg bg-white border border-slate-200 outline-none font-bold" required>
                    <option value="">👇 Select District</option>
                    {filteredDistrictsList.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
            </div>

          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input name="village" placeholder="Village / City Area" onChange={handleChange} className="p-4 rounded-xl bg-slate-50 border border-slate-200 outline-none focus:ring-2 focus:ring-blue-500" required />
            <input name="wage" type="number" placeholder="Daily Wage (₹)" onChange={handleChange} className="p-4 rounded-xl bg-slate-50 border border-slate-200 outline-none focus:ring-2 focus:ring-blue-500" required />
          </div>

          <div>
            <label className="block text-sm font-bold text-slate-500 mb-2">Worker Photo</label>
            <input type="file" onChange={handleImageChange} className="w-full p-3 bg-slate-50 rounded-xl border border-slate-200" />
          </div>

          <button disabled={loading} className="w-full bg-blue-600 text-white py-4 rounded-xl font-bold text-lg hover:bg-blue-700 shadow-lg transition transform active:scale-95">
            {loading ? 'Checking & Saving...' : '✅ Save Worker Profile'}
          </button>

        </form>

        <YouTubeHelpButton 
            text="📺 See How to Add Worker Profile" 
            url="https://youtube.com/shorts/LC_4k-L_DwM?si=Eg0j8sOEHppiTJzW" 
        />

      </div>
    </div>
  );
};

export default AddWorker;